// API Configuration
export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_URL,
  TIMEOUT: parseInt(import.meta.env.VITE_API_TIMEOUT) || 10000,
  VERSION: 'v1',
} as const

// API Endpoints - Comprehensive endpoint definitions
export const API_ENDPOINTS = {
  // Authentication & Authorization
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    REGISTER: '/auth/register',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    VERIFY_EMAIL: '/auth/verify-email',
    RESEND_VERIFICATION: '/auth/resend-verification',
  },

  // User Management
  USERS: {
    PROFILE: '/users/profile',
    UPDATE: '/users/update',
    LIST: '/users',
    CREATE: '/users',
    DELETE: (id: string) => `/users/${id}`,
    PERMISSIONS: (id: string) => `/users/${id}/permissions`,
    ROLES: '/users/roles',
    AVATAR: '/users/avatar',
  },

  // Session Management
  SESSION: {
    CURRENT: '/session',
    REFRESH: '/session/refresh',
    VALIDATE: '/session/validate',
    REVOKE: '/session/revoke',
    LIST: '/session/list',
  },

  // IVRPATH Session Management
  IVRPATH: {
    SESSION_START: '/ivrpath/session/start',
    SESSION_ACTION: '/ivrpath/session/action',
    SESSION_CURRENT: '/ivrpath/session',
    SESSION_REFRESH: '/ivrpath/session/refresh',
    SESSION_UPDATE: '/ivrpath/session/update',
    CAMPAIGNS: '/ivrpath/campaigns',
    CAMPAIGN: (id: string) => `/ivrpath/campaigns/${id}`,
  },

  // Customer Management
  CUSTOMERS: {
    LIST: '/customers/',
    DETAIL: (id: string) => `/customers/${id}/`,
    CREATE: '/customers/',
    UPDATE: (id: string) => `/customers/${id}/`,
    DELETE: (id: string) => `/customers/${id}/`,
    SEARCH: '/customers/search/',
    EXPORT: '/customers/export',
    IMPORT: '/customers/import',
  },

  // Cart & Shopping
  CART: {
    GET: (sessionId: number) => `/sessions/${sessionId}/cart/`,
    ADD_ITEM: '/cart/items',
    UPDATE_ITEM: (itemId: string) => `/cart/items/${itemId}`,
    REMOVE_ITEM: (itemId: string) => `/cart/items/${itemId}`,
    CLEAR: '/cart/clear',
    APPLY_COUPON: '/cart/coupon',
    REMOVE_COUPON: '/cart/coupon',
    ESTIMATE_SHIPPING: '/cart/shipping-estimate',
  },

  // Products & Catalog
  PRODUCTS: {
    LIST: '/products',
    DETAIL: (id: string) => `/products/${id}`,
    CREATE: '/products',
    UPDATE: (id: string) => `/products/${id}`,
    DELETE: (id: string) => `/products/${id}`,
    SEARCH: '/products/search',
    CATEGORIES: '/products/categories',
    VARIANTS: (id: string) => `/products/${id}/variants`,
    INVENTORY: (id: string) => `/products/${id}/inventory`,
    REVIEWS: (id: string) => `/products/${id}/reviews`,
  },

  // Orders & Fulfillment
  ORDERS: {
    LIST: '/orders',
    DETAIL: (id: string) => `/orders/${id}`,
    CREATE: '/orders',
    UPDATE: (id: string) => `/orders/${id}`,
    CANCEL: (id: string) => `/orders/${id}/cancel`,
    REFUND: (id: string) => `/orders/${id}/refund`,
    TRACKING: (id: string) => `/orders/${id}/tracking`,
    INVOICE: (id: string) => `/orders/${id}/invoice`,
    HISTORY: '/orders/history',
  },

  // Payments
  PAYMENTS: {
    METHODS: '/payments/methods',
    PROCESS: '/payments/process',
    REFUND: (id: string) => `/payments/${id}/refund`,
    HISTORY: '/payments/history',
    WEBHOOKS: '/payments/webhooks',
  },

  // Analytics & Reporting
  ANALYTICS: {
    DASHBOARD: '/analytics/dashboard',
    SALES: '/analytics/sales',
    CUSTOMERS: '/analytics/customers',
    PRODUCTS: '/analytics/products',
    TRAFFIC: '/analytics/traffic',
    EXPORT: '/analytics/export',
  },

  // File Management
  FILES: {
    UPLOAD: '/files/upload',
    DELETE: (id: string) => `/files/${id}`,
    GET: (id: string) => `/files/${id}`,
    LIST: '/files',
  },

  // Settings & Configuration
  SETTINGS: {
    GENERAL: '/settings/general',
    NOTIFICATIONS: '/settings/notifications',
    INTEGRATIONS: '/settings/integrations',
    API_KEYS: '/settings/api-keys',
    WEBHOOKS: '/settings/webhooks',
  },

  // Health & Monitoring
  HEALTH: {
    CHECK: '/health',
    STATUS: '/health/status',
    METRICS: '/health/metrics',
  },

  // Utility Endpoints
  UTILS: {
    COUNTRIES: '/utils/countries',
    STATES: (countryCode: string) => `/utils/countries/${countryCode}/states`,
    CURRENCIES: '/utils/currencies',
    TIMEZONES: '/utils/timezones',
    VALIDATE_ADDRESS: '/utils/validate-address',
  },
} as const

// HTTP Status Codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  ACCEPTED: 202,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  METHOD_NOT_ALLOWED: 405,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504,
} as const

// Request/Response Content Types
export const CONTENT_TYPES = {
  JSON: 'application/json',
  FORM_DATA: 'multipart/form-data',
  FORM_ENCODED: 'application/x-www-form-urlencoded',
  TEXT: 'text/plain',
  HTML: 'text/html',
  XML: 'application/xml',
} as const
